import { useCallback, useEffect, useState } from 'react'

import Action from 'ageas-react-component-action'
import CheckBox from 'ageas-react-component-checkbox'
import Dropdown from 'ageas-react-component-dropdownlist'
import Message from 'ageas-react-component-message'
import NumberField from 'ageas-react-component-numberfield'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import { useAnimalStore } from '../../Stores/animalStore'
import { useReportStore } from '../../Stores/reportStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useEntityDetailsForSummaryDocumentStore } from '../../Stores/useEntityDetailsForSummaryDocumentStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { useWarningStore } from '../../Stores/warningStore'
import { useSimulationSummaryDownload } from '../../hooks/api/simulationSummaryDownload/useSimulationSummaryDownload'
import { useSimulationSummaryDownloadPayload } from '../../hooks/api/simulationSummaryDownload/useSimulationSummaryDownloadPayload'
import { useGetAdditionalEntityDetailsForSummaryDocument } from '../../hooks/api/useGetAdditionalEntityDetailsForSummaryDocument'
import { t } from '../../i18n'
import {
    validateEmail,
    validateFullName,
    validatePhoneNumber,
} from '../../utils/validates'
import { ReportOwnerIdentification } from './ReportOwnerIdentification'
import { GenerateReportContainer } from './styles'

type DropdownOptions = {
    caption: string
    value: string
}

export type Report = {
    vatNumber?: string
    name: string
    phone: string
    email: string
    packs: string[]
    phoneList: DropdownOptions[]
    emailList: DropdownOptions[]
}

type IdentificationProps = {
    fieldsDisabled?: boolean
    onLoading: (value: boolean) => void
}

export const Identification = ({
    onLoading,
    fieldsDisabled,
}: IdentificationProps) => {
    const [hasUpdate, setHasUpdate] = useState(true)
    const [hasChangedValue, setHasChangedValue] = useState(false)
    const { setWarningOptions } = useWarningStore()
    const { animalsList } = useAnimalStore()
    const {
        isReportGenerated,
        setReportGenerated,
        setIsReportGeneratedEnabled,
        isProspect,
        report,
        setReport,
        setIsProspect,
        isNifSearched,
        setIsNifSearched,
    } = useReportStore()
    const { entitySelected, setOwnerFiscalNumber } = useSimulatorStore()
    const {
        entityDetailsForSummaryDocumentSelected,
        setEntityDetailsForSummaryDocumentSelected,
    } = useEntityDetailsForSummaryDocumentStore()
    const { runPetisSimulation } = useRunPetisSimulationStore()
    const [renderKey, setRenderKey] = useState(true)
    const [isNamePristine, setIsNamePristine] = useState(false)
    const [isPhonePristine, setIsPhonePristine] = useState(false)
    const [isEmailPristine, setIsEmailPristine] = useState(false)
    const [isGlobalEntity, setIsGlobalEntity] = useState(false)
    const [isRerenderReport, setIsRerenderReport] = useState(false)
    const { simulationSummaryDownloadPayload } =
        useSimulationSummaryDownloadPayload()
    const {
        mutate: additionalMutate,
        data: additionalData,
        isLoading: additionalIsLoading,
        isError: additionalIsError,
    } = useGetAdditionalEntityDetailsForSummaryDocument()

    const hasInitialName =
        (entityDetailsForSummaryDocumentSelected?.name || '').length > 0

    const getPackId = (packName: string) => {
        return (
            runPetisSimulation?.runPetisSimulationResponse?.insuredObjectGroup?.insuredObjects?.[0]?.packsObject?.packs?.find(
                (pack) => pack.packName === packName,
            )?.packId || ''
        )
    }

    const allPacks =
        runPetisSimulation?.runPetisSimulationResponse?.insuredObjectGroup?.insuredObjects?.[0]?.packsObject?.packs?.map(
            (pack) => pack.packName || '',
        )

    const { isLoading, isSuccess, mutate } = useSimulationSummaryDownload()

    const checkReport = useCallback(() => {
        if (
            report.vatNumber?.trim() === '' ||
            report?.name?.trim() === '' ||
            !validateFullName(report.name || '', hasInitialName) ||
            report?.phone?.trim() === '' ||
            !validatePhoneNumber(report.phone || '', true) ||
            report?.email?.trim() === '' ||
            !validateEmail(report.email || '') ||
            report?.packs?.length === 0
        ) {
            return false
        }

        return true
    }, [report])

    const handleFieldChange = useCallback(
        (fieldName: keyof Report, newValue: string | DropdownOptions[]) => {
            setReport({ [fieldName]: newValue })
        },
        [],
    )

    const handlePackChange = (pack: string) => {
        const packId = getPackId(pack)

        const updatedPacks = report?.packs?.includes(packId)
            ? report.packs.filter((item) => item !== packId)
            : [...report?.packs!, packId]

        setReport({ packs: updatedPacks })
        checkReport()
    }

    const updateReport = (partialReport: Partial<Report>) => {
        setReport({ ...partialReport })
        setRenderKey((prevKey) => !prevKey)
    }

    const nameMessage = () => {
        const nameLength = (report.name || '').length

        if (nameLength > 0) {
            if (!validateFullName(report.name || '', hasInitialName)) {
                return t('pages.step2.report.errors.invalidName')
            }
        } else if (nameLength === 0 && isNamePristine) {
            return t('pages.step3.mandatory')
        }

        return ''
    }

    const phoneMessage = () => {
        const phoneLength = (report.phone || '').length

        if (phoneLength > 0) {
            if (!validatePhoneNumber(report.phone || '', true)) {
                return t('pages.step2.report.errors.invalidPhone')
            }
        } else if (phoneLength === 0 && isPhonePristine) {
            return t('pages.step3.mandatory')
        }

        return ''
    }

    const emailMessage = () => {
        const emailLength = (report.email || '').length

        if (emailLength > 0) {
            if (!validateEmail(report.email || '')) {
                return t('pages.step3.contacts.invalidEmail')
            }
        } else if (emailLength === 0 && isEmailPristine) {
            return t('pages.step3.mandatory')
        }

        return ''
    }

    const handleReportErrorMessage = () => {
        let label = t('pages.step2.report.validation')

        label += !report.vatNumber ? t('pages.step2.report.nif') : ''
        label += !validateEmail(report.email || '')
            ? t('pages.step2.report.email')
            : ''
        label += !validateFullName(report.name || '', hasInitialName)
            ? t('pages.step2.report.name')
            : ''
        label += !validatePhoneNumber(report.phone || '', true)
            ? t('pages.step2.report.phone')
            : ''

        if (report?.packs?.length === 0) {
            label += t('pages.step2.report.packs')
        }

        if (!isNifSearched && (report.vatNumber || '')?.length > 0) {
            label = t('pages.step2.report.search')
        }

        label = label.slice(0, -1) + '.'

        setWarningOptions({
            labelTypes: [label],
        })
    }

    useEffect(() => {
        if (
            entityDetailsForSummaryDocumentSelected &&
            hasUpdate &&
            !isReportGenerated
        ) {
            additionalMutate({
                entityId:
                    entitySelected?.idEntity ||
                    entityDetailsForSummaryDocumentSelected.idEntity,
            })

            updateReport({
                vatNumber: entityDetailsForSummaryDocumentSelected.vatNumber || '',
                email:
                    entityDetailsForSummaryDocumentSelected?.email || report.email || '',
                name: entityDetailsForSummaryDocumentSelected?.name || '',
                phone: entityDetailsForSummaryDocumentSelected?.phone || '',
            })

            setRenderKey((prevKey) => !prevKey)
            setIsNifSearched(true)
            setHasUpdate(false)
        }
    }, [
        additionalData,
        isReportGenerated,
        additionalMutate,
        entityDetailsForSummaryDocumentSelected,
        hasUpdate,
    ])

    useEffect(() => {
        const animalPacksSelected = animalsList.map(
            (animal) => animal.pack?.packId || '',
        )

        if (animalPacksSelected.length > 0) {
            const packs = [...(new Set(animalPacksSelected) as never)]
            setReport({ packs })
            checkReport()
        }
    }, [])

    useEffect(() => {
        const _additionalPhoneData = additionalData?.phoneContactList || []
        const _additionalEmailData = additionalData?.emailList || []

        if (
            _additionalPhoneData?.length > 0 &&
            _additionalPhoneData?.filter((item) => item?.phoneNumber !== null)
                ?.length > 0
        ) {
            handleFieldChange(
                'phoneList',
                _additionalPhoneData?.map((item) => ({
                    caption: item?.phoneNumber || '',
                    value: item?.phoneNumber || '',
                })),
            )
            handleFieldChange(
                'emailList',
                _additionalEmailData?.map((item) => ({
                    caption: item?.email || '',
                    value: item?.email || '',
                })),
            )

            setReport({
                phone: additionalData?.phoneContactList?.[0]?.phoneNumber || '',
                email: additionalData?.emailList?.[0]?.email || '',
            })
            setRenderKey((prevKey) => !prevKey)
        }
    }, [additionalData, handleFieldChange])

    useEffect(() => {
        onLoading(isLoading || additionalIsLoading)
    }, [isLoading, additionalIsLoading, onLoading])

    useEffect(() => {
        if (isSuccess) {
            setEntityDetailsForSummaryDocumentSelected({
                ...entityDetailsForSummaryDocumentSelected,
                email: report.email,
                phone: report.phone,
            } as never)
            setRenderKey((prevKey) => !prevKey)
            setReportGenerated(true)
            setIsReportGeneratedEnabled(false)
        }
    }, [isSuccess])

    useEffect(() => {
        if ((additionalData?.phoneContactList || [])?.length > 0) {
            setIsGlobalEntity(true)
        }
    }, [additionalData])

    useEffect(() => {
        if (
            !entityDetailsForSummaryDocumentSelected &&
            (report.vatNumber || '')?.length > 0
        ) {
            setIsGlobalEntity(false)
            setIsRerenderReport(true)
            setOwnerFiscalNumber(report.vatNumber || '')
        }
    }, [entityDetailsForSummaryDocumentSelected, report])

    useEffect(() => {
        if (additionalIsError) {
            onLoading(false)
        }
    }, [additionalIsError])

    return (
        <>
            <Message type="warning" text={t('pages.step2.report.warning')} />
            <ReportOwnerIdentification
                report={report}
                updateReport={updateReport}
                onChange={(value) => handleFieldChange('vatNumber', value)}
                isRerenderReport={isRerenderReport}
                setIsGlobalEntity={setIsGlobalEntity}
            />
            <TextField
                key={'generate-report-fullname' + renderKey}
                id={`nameReport-${isNamePristine}`}
                maxLength={50}
                message={nameMessage()}
                readOnly={
                    (entityDetailsForSummaryDocumentSelected?.name || '').length > 0 ||
                    fieldsDisabled
                }
                label={t('pages.step2.generateReport.fullName')}
                value={report.name}
                onValueChanged={(id: string, value: string) => {
                    handleFieldChange('name', value)
                    setIsNamePristine(true)
                    if (
                        isGlobalEntity &&
                        entityDetailsForSummaryDocumentSelected?.name !== value
                    ) {
                        setHasChangedValue(true)
                    }
                }}
            />
            {(report.phoneList || [])?.length > 1 ? (
                <Dropdown
                    required
                    readOnly={fieldsDisabled}
                    id="idReport"
                    name="idReport"
                    label={t('pages.step2.generateReport.phone')}
                    noSort={true}
                    version="2"
                    colorTheme="blue"
                    value={report.phoneList?.[0]?.value || ''}
                    onValueChanged={(id: string, value: string) => {
                        handleFieldChange('phone', value)
                        if (isGlobalEntity && report.phoneList?.[0]?.value !== value) {
                            setHasChangedValue(true)
                        }
                    }}
                    options={report.phoneList}
                />
            ) : (
                <NumberField
                    message={phoneMessage()}
                    readOnly={fieldsDisabled}
                    key={`phoneNumberReport-${renderKey}`}
                    id={`phoneNumberReport-${renderKey}-${isPhonePristine}`}
                    label={t('pages.step2.generateReport.phone')}
                    maxLength={9}
                    colorTheme="blue"
                    mask="000000000"
                    version="2"
                    value={report.phone || report?.phoneList?.[0]?.value || ""}
                    onValueChanged={(id: string, value: string) => {
                        setIsPhonePristine(true)
                        handleFieldChange('phone', value)
                        if (isGlobalEntity && report.phone !== value) {
                            setHasChangedValue(true)
                        }
                    }}
                />
            )}
            {(report.emailList || [])?.length > 1 ? (
                <Dropdown
                    required
                    readOnly={fieldsDisabled}
                    id="idReport-email"
                    name="idReport-email"
                    label={t('pages.step2.generateReport.email')}
                    noSort={true}
                    version="2"
                    colorTheme="blue"
                    value={report.emailList?.[0]?.value || ''}
                    onValueChanged={(id: string, value: string) => {
                        handleFieldChange('email', value)
                        if (isGlobalEntity && report.emailList?.[0]?.value !== value) {
                            setHasChangedValue(true)
                        }
                    }}
                    options={report.emailList}
                />
            ) : (
                <TextField
                    key={'generate-report-email' + renderKey}
                    readOnly={
                        (!isProspect &&
                            (entityDetailsForSummaryDocumentSelected?.email || '').length >
                            0) ||
                        fieldsDisabled
                    }
                    id={`emailReport-${isEmailPristine}`}
                    name="emailReport"
                    label={t('pages.step2.generateReport.email')}
                    colorTheme="blue"
                    message={emailMessage()}
                    maxLength={70}
                    version="2"
                    value={report?.email || report?.emailList?.[0]?.value || ''}
                    onValueChanged={(id: string, value: string) => {
                        handleFieldChange('email', value)
                        setIsEmailPristine(true)
                        if (
                            isGlobalEntity &&
                            entityDetailsForSummaryDocumentSelected?.email !== value
                        ) {
                            setHasChangedValue(true)
                        }
                    }}
                />
            )}
            <Section
                id="subtitleReport"
                name={t('pages.step2.generateReport.selectionPacks')}
                config={{ labelSize: '20px', labelBold: '500' }}
                colorTheme="blue"
            />
            <div className="grid-checkbox">
                {allPacks?.map((pack, index) => (
                    <div
                        key={`${pack}+${index}` + renderKey}
                        className={fieldsDisabled ? 'grid-checkbox-item' : ''}
                    >
                        <CheckBox
                            readOnly={fieldsDisabled}
                            id={pack}
                            key={`${pack}+${index}` + renderKey}
                            label={pack}
                            value={report?.packs?.includes(getPackId(pack)) ? '1' : '0'}
                            onValueChanged={(key: any, value: string) => {
                                handlePackChange(pack)
                            }}
                        />
                    </div>
                ))}
            </div>
            <GenerateReportContainer>
                <div className="button-action">
                    <Action
                        id="actionsReport"
                        onValueChanged={() => {
                            if (checkReport() && isNifSearched) {
                                setIsProspect(
                                    entityDetailsForSummaryDocumentSelected?.isProspect || (entitySelected === null &&
                                        entityDetailsForSummaryDocumentSelected === null),
                                )
                                mutate(
                                    simulationSummaryDownloadPayload(report, hasChangedValue),
                                )
                            } else {
                                handleReportErrorMessage()
                            }
                        }}
                        disable={isLoading || additionalIsLoading || fieldsDisabled}
                        colorTheme="blue"
                        buttonType="secondary"
                        label={t('pages.step2.generateReport.btnGenerateReport')}
                    />
                </div>
            </GenerateReportContainer>
        </>
    )
}
